/**
 * Erros da API.
 *
 * O backend responde SEMPRE no mesmo envelope:
 *
 *   { "error": { "code", "message", "details", "request_id" } }
 *
 * Os códigos fazem parte do contrato público (docs/api.md) e são a base do
 * tratamento de erro no frontend.
 */

export const API_ERROR_CODES = [
  "unauthenticated",
  "invalid_credentials",
  "forbidden",
  "not_found",
  "validation_failed",
  "insufficient_credits",
  "pricing_not_configured",
  "promotion_not_available",
  "invalid_state_transition",
  "subscription_required",
  "already_unlocked",
  "idempotency_conflict",
  "idempotency_in_progress",
  "rate_limited",
  "provider_unavailable",
  "provider_not_configured",
  "internal_error",
] as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

/** Falhas que nascem no cliente, não no backend. */
export type ClientErrorCode = "network_error" | "malformed_response";

export type ErrorCode = ApiErrorCode | ClientErrorCode;

export type ErrorDetails = Record<string, unknown>;

/** Códigos desconhecidos (backend mais novo que o frontend) viram genéricos. */
export function toErrorCode(value: unknown): ErrorCode {
  return isApiErrorCode(value) ? value : "internal_error";
}

function isApiErrorCode(value: unknown): value is ApiErrorCode {
  return (
    typeof value === "string" &&
    (API_ERROR_CODES as readonly string[]).includes(value)
  );
}

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details: ErrorDetails;
  readonly requestId: string | null;

  constructor(params: {
    code: ErrorCode;
    status: number;
    message: string;
    details?: ErrorDetails;
    requestId?: string | null;
  }) {
    super(params.message);
    this.name = "ApiError";
    this.code = params.code;
    this.status = params.status;
    this.details = params.details ?? {};
    this.requestId = params.requestId ?? null;
  }

  /** Sessão ausente/expirada/revogada — o AuthProvider reage a isto. */
  get isUnauthenticated(): boolean {
    return this.code === "unauthenticated";
  }

  /** Erro de validação por campo, no formato do Rails: { campo: [msgs] }. */
  fieldErrors(): Record<string, string[]> {
    if (this.code !== "validation_failed") return {};

    const result: Record<string, string[]> = {};
    for (const [field, messages] of Object.entries(this.details)) {
      if (Array.isArray(messages)) {
        result[field] = messages.map((message) => String(message));
      } else if (typeof messages === "string") {
        result[field] = [messages];
      }
    }
    return result;
  }
}

/**
 * Mensagens voltadas ao USUÁRIO. Nunca mostramos mensagem técnica crua.
 *
 * Quando o código não é conhecido, caímos numa mensagem genérica — e o
 * `request_id` continua disponível em `ApiError` para diagnóstico.
 */
const USER_MESSAGES: Record<ErrorCode, string> = {
  unauthenticated: "Sua sessão expirou. Entre novamente para continuar.",
  invalid_credentials: "E-mail ou senha inválidos.",
  forbidden: "Você não tem permissão para executar esta ação.",
  not_found: "Não encontramos o que você procurava.",
  validation_failed: "Revise os campos destacados e tente de novo.",
  insufficient_credits: "Créditos insuficientes para esta operação.",
  pricing_not_configured:
    "Esta oportunidade ainda não tem preço configurado. Fale com o suporte.",
  promotion_not_available: "Nenhuma promoção disponível para esta conta.",
  invalid_state_transition: "Esta ação não é possível no status atual.",
  subscription_required: "É necessário ter uma assinatura ativa.",
  already_unlocked: "Esta oportunidade já foi desbloqueada.",
  idempotency_conflict:
    "Esta operação já foi enviada com outros dados. Recarregue e tente de novo.",
  idempotency_in_progress:
    "Já estamos processando esta operação. Aguarde um instante.",
  rate_limited: "Muitas tentativas. Aguarde alguns instantes e tente de novo.",
  provider_unavailable:
    "A fonte externa está indisponível no momento. Tente de novo em instantes.",
  provider_not_configured:
    "Este recurso ainda não está disponível. Fale com o suporte.",
  internal_error: "Algo deu errado do nosso lado. Tente novamente.",
  network_error:
    "Não conseguimos falar com o servidor. Verifique sua conexão e tente de novo.",
  malformed_response: "Recebemos uma resposta inesperada do servidor.",
};

export function userMessageFor(error: unknown): string {
  if (error instanceof ApiError) {
    return USER_MESSAGES[error.code] ?? USER_MESSAGES.internal_error;
  }
  return USER_MESSAGES.internal_error;
}

/**
 * Detalhe técnico para diagnóstico. Exibido apenas em desenvolvimento ou em
 * áreas explicitamente de suporte — nunca como mensagem principal ao usuário.
 */
export function technicalDetailFor(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;

  const parts = [`code=${error.code}`, `status=${error.status}`];
  if (error.requestId) parts.push(`request_id=${error.requestId}`);
  return parts.join(" · ");
}
